import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";

import {
  Grid,
  Typography as MuiTypography,
  Button,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Popover,
  Box,
} from "@material-ui/core";

import { green, red } from "@material-ui/core/colors";

import Stats from "./Stats";
import {
  Activity,
  Calendar,
  DollarSign,
  Eye,
  Users,
  Box as BoxIcon,
} from "react-feather";

import ReferallTable from "../../../components/ReferallTable";
import ReferralInvitationCard from "../../../components/ReferralInvitationCard";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { DateRange, DateRangePicker } from "react-date-range";
import { enUS, es } from "date-fns/locale";
import { addDays } from "date-fns";
import dayjs from "dayjs";
import { useTheme } from "@material-ui/core/styles";
import { useMediaQuery, Dialog, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../../utils/localization";

///// API Services /////
import {
  MediaAdsActive,
  ReferralAmount,
  ProfitReferral,
} from "../../../services/mediaAdsServices";

const DashboardSurface = styled.div`
  position: relative;
  padding: 8px 0 12px;

  &:before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    height: 320px;
    border-radius: 32px;
    background: radial-gradient(
        circle at top left,
        rgba(14, 165, 233, 0.18),
        transparent 38%
      ),
      radial-gradient(
        circle at top right,
        rgba(34, 197, 94, 0.12),
        transparent 32%
      ),
      linear-gradient(135deg, #f8fbff 0%, #eef6ff 50%, #f8fafc 100%);
    z-index: 0;
  }
`;

const HeroCard = styled(MuiCard)`
  position: relative;
  z-index: 1;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.14);
  background: linear-gradient(135deg, #0f172a 0%, #12243d 46%, #163b63 100%);
`;

const HeroContent = styled(MuiCardContent)`
  position: relative;
  padding: 32px;

  &:last-child {
    padding-bottom: 32px;
  }

  &:before,
  &:after {
    content: "";
    position: absolute;
    border-radius: 999px;
    pointer-events: none;
  }

  &:before {
    width: 260px;
    height: 260px;
    right: -60px;
    top: -110px;
    background: rgba(56, 189, 248, 0.18);
  }

  &:after {
    width: 180px;
    height: 180px;
    right: 90px;
    bottom: -100px;
    background: rgba(34, 197, 94, 0.16);
  }
`;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
`;

const HeroTitle = styled(MuiTypography)`
  color: #f8fafc;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.08;
`;

const HeroText = styled(MuiTypography)`
  max-width: 680px;
  color: rgba(226, 232, 240, 0.9);
  line-height: 1.7;
`;

const HeroActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  height: 100%;

  @media (max-width: 960px) {
    justify-content: flex-start;
    margin-top: 12px;
  }
`;

const FilterButton = styled(Button)`
  && {
    border-radius: 16px;
    padding: 12px 18px;
    min-width: 220px;
    justify-content: flex-start;
    text-transform: none;
    font-weight: 700;
    color: #0f172a;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
  }
`;

const SectionCard = styled(MuiCard)`
  position: relative;
  z-index: 1;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const SectionContent = styled(MuiCardContent)`
  padding: 0;

  &:last-child {
    padding-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SectionHeading = styled(MuiTypography)`
  color: #0f172a;
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const SectionDescription = styled(MuiTypography)`
  color: #64748b;
  max-width: 620px;
`;

const SectionPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #eff6ff;
  color: #0369a1;
  font-weight: 700;
  white-space: nowrap;
`;

function DashboardSummary() {
  const { t, i18n } = useTranslation();
  const [active_MediaAds, setMediaAdsActive] = useState();
  const [referral_amount, setAmountReferral] = useState();
  const [profitAds, setProfitAds] = useState();
  const [totalBalance, setTotalBalance] = useState(0);
  //const [buttonLabel, setButtonLabel] = useState("Hoy");

  const theme = useTheme();

  const [range, setRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);
  const [buttonLabel, setButtonLabel] = useState("");

  const localStg = JSON.parse(sessionStorage.getItem("token_store"));

  useEffect(() => {
    handleLoadData(range);
    // Dashboard data is loaded once with the initial range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleLoadData(selectedRange) {
    const start = dayjs(selectedRange[0].startDate)
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
      .split("T")[0];
    const end = dayjs(selectedRange[0].endDate)
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
      .split("T")[0];

    MediaAdsActive(end, start).then((response) =>
      setMediaAdsActive(response.activeAds)
    );

    ProfitReferral(end, start).then((response) =>
      setProfitAds(response.totalPay)
    );

    ReferralAmount().then((response) => {
      setAmountReferral(response.referralQuantity);
    });
  }

  function handleReferralRowsLoaded(rows) {
    const balance = rows.reduce((total, row) => {
      const numericBalance = Number(
        String(row.balance == null ? 0 : row.balance).replace(/[^0-9.-]/g, "")
      );

      return total + (Number.isFinite(numericBalance) ? numericBalance : 0);
    }, 0);

    setTotalBalance(balance);
  }

  function RangeDateFilter() {
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const [anchorEl, setAnchorEl] = useState(null);
    const [localRange, setLocalRange] = useState(range); // local state for picker

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleApply = () => {
      setRange(localRange); // update parent
      setButtonLabel(
        `${
          dayjs(localRange[0].startDate)
            .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
            .split("T")[0]
        } – ${
          dayjs(localRange[0].endDate)
            .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
            .split("T")[0]
        }`
      );
      console.log(localRange);
      handleLoadData(localRange);
      setAnchorEl(null); // close only on Apply
    };

    return (
      <React.Fragment>
        <Button
          component={FilterButton}
          variant="contained"
          aria-owns={anchorEl ? "simple-menu" : undefined}
          aria-haspopup="true"
          onClick={handleClick}
          startIcon={<Calendar size={16} />}
        >
          {buttonLabel || t("dashboard.dateRange")}
        </Button>
        {isMobile ? (
          <Dialog open={Boolean(anchorEl)} onClose={handleClose} fullWidth>
            <DialogContent>
              <DateRange
                editableDateInputs
                onChange={(item) => setLocalRange([item.selection])}
                moveRangeOnFirstSelection={false}
                ranges={localRange}
                locale={i18n.resolvedLanguage === "en" ? enUS : es}
                maxDate={new Date()} //max date, today
                minDate={addDays(new Date(), -366)}
              />
              <Button
                onClick={handleApply}
                color="primary"
                variant="contained"
                fullWidth
                style={{ marginTop: 8 }}
              >
                {t("common.apply")}
              </Button>
            </DialogContent>
          </Dialog>
        ) : (
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            disableRestoreFocus
          >
            <div style={{ padding: 8 }}>
              <DateRangePicker
                showSelectionPreview
                direction="horizontal"
                preventSnapRefocus
                calendarFocus="backwards"
                editableDateInputs
                onChange={(item) => setLocalRange([item.selection])}
                moveRangeOnFirstSelection={false}
                ranges={localRange}
                locale={i18n.resolvedLanguage === "en" ? enUS : es}
                maxDate={new Date()} //max date, today
                minDate={addDays(new Date(), -366)}
              />
              <Button
                onClick={handleApply}
                color="primary"
                variant="contained"
                fullWidth
                style={{ marginTop: 8 }}
              >
                {t("common.apply")}
              </Button>
            </div>
          </Popover>
        )}
      </React.Fragment>
    );
  }

  return (
    <DashboardSurface>
      <HeroCard>
        <HeroContent>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={8}>
              <HeroEyebrow>
                <Activity size={14} />
                {t("dashboard.growthPanel")}
              </HeroEyebrow>
              <HeroTitle variant="h2" mt={4} mb={2}>
                {t("dashboard.welcome", {
                  name: `${localStg.name} ${localStg.lastName}`,
                })}
              </HeroTitle>
              <HeroText variant="body1">{t("dashboard.description")}</HeroText>
            </Grid>
            <Grid item xs={12} md={4}>
              <HeroActions>
                <RangeDateFilter />
              </HeroActions>
            </Grid>
          </Grid>
        </HeroContent>
      </HeroCard>

      <Grid
        container
        spacing={6}
        style={{ position: "relative", zIndex: 1, marginTop: 8 }}
      >
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title={t("dashboard.totalReferrals")}
            amount={referral_amount}
            percentageText="+26%"
            percentagecolor={green[500]}
            IconComponent={BoxIcon}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title={t("dashboard.activeAds")}
            amount={active_MediaAds}
            percentageText="-14%"
            percentagecolor={red[500]}
            IconComponent={Eye}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title={t("dashboard.totalBalance")}
            amount={formatCurrency(totalBalance)}
            percentageText={t("dashboard.accumulatedTotal")}
            percentagecolor={green[500]}
            IconComponent={DollarSign}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={3} xl>
          <Stats
            title={t("dashboard.totalEarnings")}
            amount={profitAds}
            percentageText="+18%"
            percentagecolor={green[500]}
            IconComponent={Users}
          />
        </Grid>
      </Grid>

      <ReferralInvitationCard />

      <SectionCard style={{ marginTop: 32 }}>
        <SectionContent>
          <SectionHeader>
            <Box>
              <SectionHeading variant="h4">
                {t("referrals.title")}
              </SectionHeading>
              <SectionDescription variant="body2">
                {t("referrals.listDescription")}
              </SectionDescription>
            </Box>
            <SectionPill>
              <Users size={16} />
              {t("referrals.activeProfiles", { count: referral_amount || 0 })}
            </SectionPill>
          </SectionHeader>
          <ReferallTable onRowsLoaded={handleReferralRowsLoaded} />
        </SectionContent>
      </SectionCard>
    </DashboardSurface>
  );
}

function Default() {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Helmet title={t("dashboard.title")} />
      <DashboardSummary />
    </React.Fragment>
  );
}

export default Default;
